"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.prepareValueType = void 0;
const error_classes_1 = __importDefault(require("../error-classes"));
const lexers_1 = __importDefault(require("../lexers"));
const functions_1 = __importDefault(require("../functions"));
const token_array_processors_1 = require("../tokens/token-array-processors");
/*
   Value Types come in two flavours: Function Types and Custom Types.

   Function Types reference a Value Function via a functionId.
   - the Value Function has ruleTypes and can reference a function
     in the codebase via a functionCodeKey

   Custom Types have fields that reference other Value Types.
   - to evaluate a Custom Type, you first need to evaluate its fields,
     via recursive calls to prepareValueType.
 */
// Check the type exists and then defer to the correct prepare function.
// Returns an array of prepared value type items.
function prepareValueType({ name, type, rules }, lookups) {
    const valueType = lookups.valueTypeSpecs[type];
    if (!valueType) {
        const message = `The Value Type for '${name}' could not be found.`;
        throw new error_classes_1.default.ValueTypeError(message);
    }
    // Each Value Type should specify a functionId or define its nested fields.
    const { functionId, fields } = valueType;
    // If the Value Type has a functionId, we assume it's a Function Type.
    if (functionId)
        return prepareFunctionType(name, rules, lookups, functionId);
    if (fields)
        return prepareCustomType(rules, lookups, fields);
    throw new error_classes_1.default.ValueTypeError(`The Value Type for '${name}' has no function or fields specified.`);
}
exports.prepareValueType = prepareValueType;
function prepareFunctionType(name, rules, lookups, functionId) {
    const valueFunctionSpec = lookups.valueFunctionSpecs[functionId];
    if (!valueFunctionSpec)
        throw new error_classes_1.default.ValueTypeError(`The Value Function for '${name}' could not be found.`);
    const codeKey = valueFunctionSpec.functionCodeKey;
    // @ts-ignore
    const fnInCodeBase = codeKey === '--No Code Key--'
        ? (rules) => rules
        : functions_1.default[codeKey];
    if (!fnInCodeBase)
        throw new error_classes_1.default.ValueFunctionError(`The specified function could not be found: [${codeKey}].`);
    let dependencies = new Set();
    const ruleEntries = [];
    for (const [ruleName, ruleType] of Object.entries(valueFunctionSpec.ruleTypes)) {
        const { getValue, dependencies: ruleDependencies } = prepareRuleExpression(ruleName, rules, ruleType, lookups);
        dependencies = new Set([...dependencies, ...ruleDependencies]);
        ruleEntries.push([ruleName, getValue]);
    }
    const evaluate = (state) => {
        const evaluatedRules = Object.fromEntries(ruleEntries.map(([ruleName, ruleFn]) => [ruleName, ruleFn(state)]));
        // @ts-ignore
        return codeKey ? fnInCodeBase(evaluatedRules) : evaluatedRules;
    };
    // Although Function Types only return a single item, we wrap the item in an array
    // to be consistent with Custom Types which might have multiple items.
    return [{
            evaluate,
            dependencies: [...dependencies],
        }];
}
function prepareCustomType(rules, lookups, fields) {
    const preparedCustomTypes = [];
    for (const [fieldName, valueTypeId] of Object.entries(fields)) {
        const nestedValueSpec = {
            name: fieldName,
            type: valueTypeId,
            rules: rules[fieldName]
        };
        const valueTypeItems = prepareValueType(nestedValueSpec, lookups);
        let dependencies = new Set();
        for (const valueTypeItem of valueTypeItems) {
            const { name: nestedName, evaluate, dependencies: nestedDeps } = valueTypeItem;
            dependencies = new Set([...dependencies, ...nestedDeps]);
            preparedCustomTypes.push({
                name: nestedName ? `${fieldName}.${nestedName}` : fieldName,
                evaluate,
                dependencies,
            });
        }
    }
    return preparedCustomTypes;
}
const processorLookup = {
    anyValue: token_array_processors_1.processAnyValueTokens,
    boolean: token_array_processors_1.processAnyValueTokens,
    textWithPlaceholders: token_array_processors_1.processTextExpressionTokens,
    textExpression: token_array_processors_1.processTextExpressionTokens,
    tokenArray: token_array_processors_1.processTokenExpressionTokens,
    expression: token_array_processors_1.processExpressionTokens,
    integerExpression: token_array_processors_1.processExpressionTokens,
    expressionList: token_array_processors_1.processExpressionListTokens,
};
function prepareRuleExpression(ruleName, rules, ruleType, lookups) {
    var _a;
    const ruleExpression = rules === null || rules === void 0 ? void 0 : rules[ruleName];
    // if (!ruleExpression) throw new errorClasses.ValueRuleError(
    //    `The '${ruleName}' rule has not been specified.`
    // );
    const getTokens = lexers_1.default[ruleType];
    if (!getTokens)
        throw new error_classes_1.default.ValueRuleError(`The '${ruleName}' rule has an unknown rule type: '${ruleType}'.`);
    const tokens = getTokens(ruleExpression);
    return ((_a = processorLookup[ruleType]) !== null && _a !== void 0 ? _a : processorLookup.expression)(tokens, ruleName, lookups);
}
